import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import {
  connectMultipleSDCs,
  disconnectMultipleSDCAll,
  setupMessageHandler,
} from "../../../utils/mqttUtils";

import call_assist_mp3 from "../../../assets/sounds/call_assist.mp3";
console.log(call_assist_mp3);

interface SDC {
  autoSdcId: number;
  sdcUuid: string;
  sdcSerialNumber: string;
  doorPlace: string;
  isEnabled: boolean;
  isSpeakerEnabled: boolean;
}

interface AssistanceRequest {
  uuid: string;
  doorPlace: string;
  userType: number;
  timestamp: string;
  confirmed: boolean;
  declined?: boolean;
}

interface AssistanceMonitorProps {
  sdcs: SDC[];
  buildingId: string; // ビルごとの識別子を受け取る
}

const AssistanceMonitor: React.FC<AssistanceMonitorProps> = ({
  sdcs,
  buildingId,
}) => {
  const [currentRequest, setCurrentRequest] =
    useState<AssistanceRequest | null>(null);

  // 音声再生設定
  const [isAudioAllowed, setIsAudioAllowed] = useState(false);

  const [audioContext] = useState(() => {
    try {
      return new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      console.error("AudioContext creation failed:", error);
      return null;
    }
  });
  const [audioBuffer, setAudioBuffer] = useState<AudioBuffer | null>(null);

  // useEffect(() => {
  //   if (!audioContext) return;

  //   fetch(call_assist_mp3)
  //     .then((response) => {
  //       if (!response.ok)
  //         throw new Error(`HTTP error! status: ${response.status}`);
  //       return response.arrayBuffer();
  //     })
  //     .then((arrayBuffer) => audioContext.decodeAudioData(arrayBuffer))
  //     .then((decodedBuffer) => setAudioBuffer(decodedBuffer))
  //     .catch((error) => {
  //       console.error("音声ファイルの読み込みに失敗しました:", error.message);
  //     });
  // }, [audioContext]);

  useEffect(() => {
    if (!audioContext) return;

    const sampleRate = audioContext.sampleRate;
    const duration = 0.6; // 合計0.6秒の音（4回分）
    const numChannels = 1;
    const buffer = audioContext.createBuffer(
      numChannels,
      sampleRate * duration,
      sampleRate
    );

    const channelData = buffer.getChannelData(0);

    const baseFrequency = 660; // 基本周波数（660Hz、中音）
    const secondFrequency = 880; // 別の周波数（880Hz、高音）
    const segmentDuration = duration / 4; // 各音の長さ（0.15秒）

    for (let i = 0; i < channelData.length; i++) {
      const time = i / sampleRate;
      const segmentIndex = Math.floor(time / segmentDuration);
      const segmentTime = time % segmentDuration;

      // どの音を生成するかを分岐
      const frequency =
        segmentIndex % 2 === 0 ? baseFrequency : secondFrequency;

      // サイン波（基本周波数）
      const sineWave = Math.sin(2 * Math.PI * frequency * segmentTime);

      // 倍音を追加
      const overtone1 =
        0.6 * Math.sin(2 * Math.PI * frequency * 2 * segmentTime); // 2倍の周波数
      const overtone2 =
        0.3 * Math.sin(2 * Math.PI * frequency * 3 * segmentTime); // 3倍の周波数

      // エンベロープ（音量の変化）
      const envelope = Math.exp(-segmentTime * 60); // 短く減衰

      // 波形を合成し、エンベロープを適用
      channelData[i] = (sineWave + overtone1 + overtone2) * envelope;
    }

    setAudioBuffer(buffer);
  }, [audioContext]);

  const playSound = useCallback(() => {
    if (!audioContext || !audioBuffer) {
      console.error("音声の再生に必要なリソースが利用できません");
      return;
    }

    try {
      if (audioContext.state === "suspended") {
        audioContext.resume();
      }

      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      source.start(0);
    } catch (error) {
      console.error("音声の再生中にエラーが発生しました:", error);
    }
  }, [audioContext, audioBuffer]);

  const requestAudioPermission = useCallback(async () => {
    if (audioContext?.state === "suspended") {
      try {
        await audioContext.resume();
        setIsAudioAllowed(true); // 状態を明示的に更新
        console.log("AudioContext resumed");
      } catch (error) {
        console.error("Failed to resume AudioContext:", error);
      }
    }
  }, [audioContext]);

  useEffect(() => {
    if (audioContext?.state === "running") {
      setIsAudioAllowed(true); // AudioContextがすでに有効ならアラートを消す
    }
  }, [audioContext]);

  // ローカルストレージキーをビルごとに生成
  const LOCAL_STORAGE_KEY = `requestHistory_${buildingId}`;

  // 初期状態をローカルストレージから読み取る
  const [requestHistory, setRequestHistory] = useState<AssistanceRequest[]>(
    () => {
      try {
        const savedHistory = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (savedHistory) {
          console.log("Loaded from localStorage:", savedHistory);
          return JSON.parse(savedHistory) as AssistanceRequest[];
        }
      } catch (error) {
        console.error("Error reading from localStorage:", error);
      }
      return []; // デフォルト値
    }
  );

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (
      currentRequest &&
      !currentRequest.confirmed &&
      !currentRequest.declined &&
      currentRequest.timestamp === requestHistory[0]?.timestamp // 最新のリクエストのみ通知音
    ) {
      interval = setInterval(() => {
        playSound();
      }, 2000); // 2秒間隔で通知音を再生
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentRequest, playSound, requestHistory]);

  const [isConnected, setIsConnected] = useState(false);
  const [selectedSDCs, setSelectedSDCs] = useState<Set<string>>(new Set());

  // ログをローカルストレージに保存（requestHistoryが変更されるたびに保存）
  useEffect(() => {
    console.log("Saving to localStorage:", requestHistory);
    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify(requestHistory.slice(0, 1000)) // 最新1000件を保存
    );
  }, [requestHistory, LOCAL_STORAGE_KEY]); // LOCAL_STORAGE_KEYを依存配列に追加

  // SDC選択の初期化
  useEffect(() => {
    if (sdcs.length > 0) {
      setSelectedSDCs(new Set(sdcs.map((sdc) => sdc.sdcUuid)));
    }
  }, [sdcs]);

  // MQTT接続の管理
  useEffect(() => {
    if (!sdcs || sdcs.length === 0) {
      console.log("SDCsが利用できません");
      return;
    }

    const selectedSdcsForMqtt = sdcs
      .filter((sdc) => selectedSDCs.has(sdc.sdcUuid) && sdc.isSpeakerEnabled) // isSpeakerEnabled が true のみ接続
      .map((sdc) => ({
        uuid: sdc.sdcUuid,
        doorPlace: sdc.doorPlace,
      }));

    if (selectedSdcsForMqtt.length === 0) {
      setIsConnected(false);
      return;
    }

    console.log("Connecting with selected SDCs:", selectedSdcsForMqtt);

    const client = connectMultipleSDCs(selectedSdcsForMqtt);
    if (client) {
      client.on("connect", () => {
        setIsConnected(true);
        console.log("MQTT接続が確立されました");
      });

      setupMessageHandler(client, (topic: string, message: Buffer) => {
        try {
          const messageObj = JSON.parse(message.toString());
          if (messageObj.state === "sci_speaker_request") {
            const uuid = topic.split("/")[2];
            const sdc = sdcs.find((s: SDC) => s.sdcUuid === uuid);

            if (sdc) {
              const newRequest: AssistanceRequest = {
                uuid,
                doorPlace: sdc.doorPlace,
                userType: messageObj.user_type,
                timestamp: messageObj.ts,
                confirmed: false,
                declined: false, // 新しいリクエストはデフォルトで非対応ではない
              };

              setRequestHistory((prev) => [newRequest, ...prev].slice(0, 1000));
              setCurrentRequest(newRequest); // 上書き表示
              playSound(); // 音声通知を再生
            }
          }
        } catch (error) {
          console.error("メッセージ処理エラー:", error);
        }
      });
    }

    return () => {
      disconnectMultipleSDCAll();
      setIsConnected(false);
    };
  }, [sdcs, selectedSDCs, playSound]);

  const handleSDCToggle = (sdcUuid: string) => {
    const newSelected = new Set(selectedSDCs);
    if (newSelected.has(sdcUuid)) {
      newSelected.delete(sdcUuid);
    } else {
      newSelected.add(sdcUuid);
    }
    setSelectedSDCs(newSelected);
  };

  const handleConfirm = async () => {
    if (!currentRequest) return;

    try {
      const jwtToken = await getJwtTokenFromIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/sdc/${currentRequest.uuid}/call-speaker`,
        {
          method: "POST",
          headers: {
            Authorization: jwtToken,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        setRequestHistory((prev) =>
          prev.map((req) =>
            req.uuid === currentRequest.uuid &&
            req.timestamp === currentRequest.timestamp
              ? { ...req, confirmed: true }
              : req
          )
        );
        setCurrentRequest(null);
      }
    } catch (error) {
      console.error("Error confirming assistance request:", error);
    }
  };

  const handleDecline = () => {
    if (!currentRequest) return;

    setRequestHistory((prev) =>
      prev.map((req) =>
        req.uuid === currentRequest.uuid &&
        req.timestamp === currentRequest.timestamp
          ? { ...req, confirmed: false, declined: true }
          : req
      )
    );

    setCurrentRequest(null); // 現在のリクエストをクリア
  };

  const handleSelectRequest = (request: AssistanceRequest) => {
    if (!request.confirmed) {
      setCurrentRequest(request);
    }
  };

  const translateUserType = (value: number): string => {
    const types: { [key: number]: string } = {
      0: "支援なし（または無変更）",
      1: "荷物多い",
      2: "お年寄り",
      3: "妊婦",
      4: "ベビーカー",
      5: "オストメイト",
      6: "聴覚障害",
      7: "視覚障害",
      8: "杖、松葉杖",
      9: "車いす",
      10: "電動車いす",
    };
    return types[value] ?? "未定義";
  };

  if (!isConnected) {
    return <div>SDCに接続中...</div>; // 接続中の場合
  }

  return (
    <div>
      <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: "#f8f9fa" }}>
        <Typography variant="h6" sx={{ mb: 2, color: "#1976d2" }}>
          表示ドア
        </Typography>
        <FormGroup
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
            gap: 2,
          }}
        >
          {sdcs
            .filter((sdc) => sdc.isSpeakerEnabled)
            .map((sdc) => (
              <Paper
                key={sdc.sdcUuid}
                elevation={1}
                sx={{
                  p: 1,
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: "rgba(25, 118, 210, 0.04)",
                  },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedSDCs.has(sdc.sdcUuid)}
                      onChange={() => handleSDCToggle(sdc.sdcUuid)}
                      sx={{
                        color: "#1976d2",
                        "&.Mui-checked": {
                          color: "#1976d2",
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {sdc.doorPlace}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {sdc.sdcSerialNumber}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            ))}
        </FormGroup>
      </Paper>

      {currentRequest && (
        <Card sx={{ bgcolor: "#fff3e0", marginBottom: 2 }}>
          <CardHeader
            title="介助要請受信中"
            action={
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleConfirm}
                  sx={{ marginRight: 1 }}
                >
                  対応する
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleDecline}
                >
                  対応しない
                </Button>
              </>
            }
          />

          <CardContent>
            <p>設置場所: {currentRequest.doorPlace}</p>
            <p>ユーザー種別: {translateUserType(currentRequest.userType)}</p>
            <p>
              受信時刻: {new Date(currentRequest.timestamp).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader
          title="ログ"
          subheader="（このデバイスで取得した最新ログ1000件がこのデバイスにのみ保存されます）"
        />

        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>日時</TableCell>
                <TableCell>設置場所</TableCell>
                <TableCell>ユーザー種別</TableCell>
                <TableCell>状態</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requestHistory.map((request: AssistanceRequest, index: any) => (
                // ログテーブルの行のスタイリング
                <TableRow
                  key={`${request.uuid}-${request.timestamp}`}
                  hover={!request.confirmed && !request.declined} // 対応済みと非対応済みはホバー無効
                  onClick={() => {
                    if (!request.confirmed && !request.declined) {
                      handleSelectRequest(request);
                    }
                  }}
                  sx={{
                    cursor:
                      !request.confirmed && !request.declined
                        ? "pointer"
                        : "default",
                    bgcolor: (theme) => {
                      if (
                        currentRequest &&
                        currentRequest.uuid === request.uuid &&
                        currentRequest.timestamp === request.timestamp
                      ) {
                        return "rgba(25, 118, 210, 0.2)"; // 選択中のデフォルト背景色
                      }
                      return request.confirmed || request.declined
                        ? "rgba(0, 0, 0, 0.04)" // 対応済み・非対応済みの共通背景
                        : "rgba(232, 244, 253, 0.6)"; // 未対応の背景
                    },
                    // 選択中の行にアニメーションを適用
                    animation:
                      currentRequest &&
                      currentRequest.uuid === request.uuid &&
                      currentRequest.timestamp === request.timestamp
                        ? "glow 1.5s infinite"
                        : "none",
                    "@keyframes glow": {
                      "0%": { backgroundColor: "rgba(25, 118, 210, 0.2)" },
                      "50%": { backgroundColor: "rgba(25, 118, 210, 0.4)" },
                      "100%": { backgroundColor: "rgba(25, 118, 210, 0.2)" },
                    },
                    "&:hover": {
                      bgcolor: (theme) => {
                        if (!request.confirmed && !request.declined) {
                          return "rgba(232, 244, 253, 0.8)"; // 未対応のホバー
                        }
                        return "inherit"; // 対応済み・非対応済みはホバーなし
                      },
                    },
                    borderLeft:
                      currentRequest &&
                      currentRequest.uuid === request.uuid &&
                      currentRequest.timestamp === request.timestamp
                        ? "4px solid #1976d2"
                        : "none",
                  }}
                >
                  <TableCell>
                    {new Date(request.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell>{request.doorPlace}</TableCell>
                  <TableCell>{translateUserType(request.userType)}</TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        color:
                          request.confirmed || request.declined
                            ? "text.secondary"
                            : "error.main", // 対応済み・非対応済みは同じ色
                        fontWeight:
                          request.confirmed || request.declined
                            ? "normal"
                            : 500,
                      }}
                    >
                      {request.declined
                        ? "非対応済み"
                        : request.confirmed
                        ? "対応済み"
                        : "未対応"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {!isAudioAllowed && (
        <Snackbar
          open={!isAudioAllowed} // 状態依存でアラートを制御
          autoHideDuration={null}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{
            "& .MuiSnackbarContent-root": {
              fontSize: "1rem",
              padding: "16px 24px",
            },
          }}
        >
          <Alert
            severity="info"
            action={
              <Button
                color="inherit"
                sx={{ fontSize: "1rem", padding: "12px 24px" }}
                onClick={requestAudioPermission}
              >
                許可する
              </Button>
            }
            sx={{
              fontSize: "1rem",
              padding: "16px",
            }}
          >
            音声通知を許可してください。
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};

export default AssistanceMonitor;
